import React, { useState, useEffect } from "react";
import {
  doc, getDoc, setDoc,
  collection, query, orderBy, limit, getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ── Defaults (must match SYSTEM_PROMPT_DEFAULT / TONE_DEFAULTS in review_ai.py) ──

const DEFAULTS = {
  systemPrompt:
    "Du schreibst im Namen des Teams von " + SHOP.name + ", einem deutschen Etsy-Shop " +
    "für personalisierte Kerzen, Wachssiegelstempel und Geschenkartikel. " +
    "Dein Ton ist herzlich, persönlich und professionell — wie ein kleines Team, " +
    "nicht ein Konzern-Kundenservice. " +
    "Schreibe sehr kurze Antworten (1–2 Sätze bei positiven Bewertungen). " +
    "Antworte immer auf Deutsch. " +
    "Verwende 'wir' und 'unser' — nie 'ich'.",
  toneHigh:   "Schreibe eine herzliche Danksagung in 1–2 kurzen Sätzen.",
  toneMedium: "Erkenne die gemischte Erfahrung neutral an (1–2 Sätze).",
  toneLow:    "Entschuldige dich aufrichtig und biete Hilfe an (2–3 Sätze).",
};

const FIELDS = [
  {
    key: "systemPrompt",
    label: "System-Prompt (Hauptanweisung)",
    hint: "Beschreibt der KI ihre Rolle, Tonalität und Sprache.",
    rows: 6,
  },
  {
    key: "toneHigh",
    label: "Ton bei 4–5 Sternen",
    hint: "Anweisung für positive Bewertungen.",
    rows: 2,
  },
  {
    key: "toneMedium",
    label: "Ton bei 3 Sternen",
    hint: "Anweisung für gemischte Bewertungen.",
    rows: 2,
  },
  {
    key: "toneLow",
    label: "Ton bei 1–2 Sternen",
    hint: "Anweisung für negative Bewertungen.",
    rows: 2,
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const ReviewAiSettings = () => {
  const [values, setValues]         = useState({ ...DEFAULTS });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [recentReplies, setRecentReplies] = useState([]);

  useEffect(() => {
    // Load prompt config
    getDoc(doc(db, "config", "reviewAiPrompt"))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setValues({
            systemPrompt: data.systemPrompt || DEFAULTS.systemPrompt,
            toneHigh:     data.toneHigh     || DEFAULTS.toneHigh,
            toneMedium:   data.toneMedium   || DEFAULTS.toneMedium,
            toneLow:      data.toneLow      || DEFAULTS.toneLow,
          });
        }
      })
      .catch((err) => console.error("ReviewAiSettings load error:", err))
      .finally(() => setLoading(false));

    // Load last 15 posted replies for prompt preview (same query as the CF)
    getDocs(query(
      collection(db, "etsy_reviews"),
      orderBy("posted_at", "desc"),
      limit(20),
    )).then((snap) => {
      const replies = snap.docs
        .filter((d) => d.data().status === "posted")
        .slice(0, 15)
        .map((d) => {
          const rd = d.data();
          return (rd.final_reply || rd.ai_reply || "").trim();
        })
        .filter(Boolean);
      setRecentReplies(replies);
    }).catch(() => {});
  }, []);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleReset = (key) => {
    setValues((prev) => ({ ...prev, [key]: DEFAULTS[key] }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await setDoc(doc(db, "config", "reviewAiPrompt"), values, { merge: true });
      setSaved(true);
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    display: "block", fontWeight: "700", fontSize: "0.8rem",
    color: "#4a5568", marginBottom: "0.2em",
    textTransform: "uppercase", letterSpacing: "0.04em",
  };
  const hintStyle = {
    fontSize: "0.75rem", color: "#a0aec0", marginBottom: "0.4em",
  };
  const taStyle = {
    width: "100%", boxSizing: "border-box",
    padding: "0.65em 0.85em",
    border: "1px solid #c8a882", borderRadius: "6px",
    fontSize: "0.875rem", color: "#2d3748", background: "#fffdf9",
    resize: "vertical", fontFamily: "inherit", lineHeight: "1.5",
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 0.5em", fontSize: "1.25rem", color: "#1a202c" }}>
        KI-Antwort-Einstellungen
      </h2>

      {/* Info banner */}
      <div style={{
        background: "#fffbeb", border: "1px solid #f6e05e",
        borderRadius: "8px", padding: "0.65em 1em",
        marginBottom: "1.5em", fontSize: "0.8rem", color: "#744210",
      }}>
        Diese Einstellungen werden beim nächsten Bewertungs-Sync verwendet.
        Bereits generierte Entwürfe werden nicht rückwirkend geändert.
      </div>

      {loading ? (
        <p style={{ color: "#718096", fontStyle: "italic" }}>Lade Einstellungen…</p>
      ) : (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "1.5em",
        }}>
          {FIELDS.map(({ key, label, hint, rows }) => (
            <div key={key} style={{ marginBottom: "1.5em" }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "baseline", marginBottom: "0.2em",
              }}>
                <label style={labelStyle}>{label}</label>
                <button
                  onClick={() => handleReset(key)}
                  style={{
                    fontSize: "0.72rem", color: "#a0aec0", background: "none",
                    border: "none", cursor: "pointer", padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Standard wiederherstellen
                </button>
              </div>
              <p style={hintStyle}>{hint}</p>
              <textarea
                rows={rows}
                value={values[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                style={taStyle}
              />
            </div>
          ))}

          <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "0.55em 1.6em", borderRadius: "6px", border: "none",
                background: saving ? "#a0aec0" : "#276749",
                color: "#fff", fontWeight: "700", fontSize: "0.875rem",
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Wird gespeichert…" : "Speichern"}
            </button>
            {saved && (
              <span style={{ fontSize: "0.82rem", color: "#276749" }}>
                ✓ Gespeichert
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Prompt-Vorschau (read-only) ───────────────────────────────── */}
      <div style={{ marginTop: "2em" }}>
        <h3 style={{
          fontSize: "0.95rem", fontWeight: "700", color: "#4a5568",
          textTransform: "uppercase", letterSpacing: "0.04em",
          marginBottom: "0.5em",
        }}>
          Aktueller Prompt (Vorschau)
        </h3>
        <p style={{ fontSize: "0.75rem", color: "#a0aec0", marginBottom: "1em" }}>
          So wird der Prompt für die nächste Bewertungsantwort zusammengebaut.
          Die kursiven Platzhalter werden durch echte Werte der jeweiligen Bewertung ersetzt.
          Der untere Block zeigt die zuletzt geposteten Antworten, die der KI
          zur Orientierung mitgegeben werden.
        </p>

        {/* System prompt */}
        <div style={{ marginBottom: "1em" }}>
          <div style={{ ...labelStyle, marginBottom: "0.3em" }}>System</div>
          <pre style={{
            background: "#f7fafc", border: "1px solid #e2e8f0",
            borderRadius: "6px", padding: "0.75em 1em",
            fontSize: "0.78rem", color: "#4a5568",
            whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
            fontFamily: "inherit", lineHeight: "1.6",
          }}>
            {loading ? "…" : values.systemPrompt}
          </pre>
        </div>

        {/* User message */}
        <div>
          <div style={{ ...labelStyle, marginBottom: "0.3em" }}>User</div>
          <pre style={{
            background: "#f7fafc", border: "1px solid #e2e8f0",
            borderRadius: "6px", padding: "0.75em 1em",
            fontSize: "0.78rem", color: "#4a5568",
            whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
            fontFamily: "inherit", lineHeight: "1.6",
          }}>
            {loading ? "…" : [
              values.toneHigh,
              "",
              "Bewerterin: \u00ABVorname\u00BB",
              "Produkt: \u00ABProduktname\u00BB",
              "Bewertung: 5/5 Sterne",
              "Rezension: \u00ABBewertungstext\u00BB",
              ...(recentReplies.length > 0 ? [
                "",
                "Letzte gepostete Antworten (bitte nicht wiederholen):",
                ...recentReplies.map((r, i) => `${i + 1}. "${r}"`),
              ] : []),
            ].join("\n")}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ReviewAiSettings;
